import * as flatbuffers from "flatbuffers"
import { Binding as FbBinding } from "./flatbuffers/cogo/renderpack/v1/binding.js"
import { DesignOp as FbDesignOp } from "./flatbuffers/cogo/renderpack/v1/design-op.js"
import { Diagnostic as FbDiagnostic } from "./flatbuffers/cogo/renderpack/v1/diagnostic.js"
import { LayoutOp as FbLayoutOp } from "./flatbuffers/cogo/renderpack/v1/layout-op.js"
import { Node as FbNode } from "./flatbuffers/cogo/renderpack/v1/node.js"
import { PropOp as FbPropOp } from "./flatbuffers/cogo/renderpack/v1/prop-op.js"
import { RenderPack as FbRenderPack } from "./flatbuffers/cogo/renderpack/v1/render-pack.js"
import { ValueType as FbValueType } from "./flatbuffers/cogo/renderpack/v1/value-type.js"
import type {
  RenderPack,
  RenderPackBinding,
  RenderPackDesignOp,
  RenderPackDiagnostic,
  RenderPackLayoutOp,
  RenderPackNode,
  RenderPackPropOp,
  RenderPackValue,
} from "./render-pack"

const FILE_IDENTIFIER = "RPK1"

export function encodeRenderPackFlatBuffer(pack: RenderPack): Uint8Array {
  const builder = new flatbuffers.Builder(1024)

  const schemaVersionOffset = builder.createString(pack.schemaVersion)
  const componentIdsOffset = createStringVector(builder, pack.componentIds, FbRenderPack.createComponentIdsVector)
  const actionIdsOffset = createStringVector(builder, pack.actionIds, FbRenderPack.createActionIdsVector)
  const tokenIdsOffset = createStringVector(builder, pack.tokenIds, FbRenderPack.createTokenIdsVector)
  const bindingIdsOffset = createStringVector(builder, pack.bindingIds, FbRenderPack.createBindingIdsVector)
  const nodesOffset = FbRenderPack.createNodesVector(builder, pack.nodes.map(node => createNodeTable(builder, node)))
  const layoutOpsOffset = FbRenderPack.createLayoutOpsVector(
    builder,
    pack.layoutOps.map(op => createLayoutOpTable(builder, op)),
  )
  const propOpsOffset = FbRenderPack.createPropOpsVector(
    builder,
    pack.propOps.map(op => createPropOpTable(builder, op)),
  )
  const designOpsOffset = FbRenderPack.createDesignOpsVector(
    builder,
    pack.designOps.map(op => createDesignOpTable(builder, op)),
  )
  const bindingsOffset = FbRenderPack.createBindingsVector(
    builder,
    pack.bindings.map(binding => createBindingTable(builder, binding)),
  )
  const diagnosticsOffset = FbRenderPack.createDiagnosticsVector(
    builder,
    pack.diagnostics.map(diagnostic => createDiagnosticTable(builder, diagnostic)),
  )

  const root = FbRenderPack.createRenderPack(
    builder,
    schemaVersionOffset,
    componentIdsOffset,
    actionIdsOffset,
    tokenIdsOffset,
    bindingIdsOffset,
    nodesOffset,
    layoutOpsOffset,
    designOpsOffset,
    bindingsOffset,
    diagnosticsOffset,
    propOpsOffset,
  )
  FbRenderPack.finishRenderPackBuffer(builder, root)
  return builder.asUint8Array()
}

export function decodeRenderPackFlatBuffer(bytes: Uint8Array): RenderPack {
  const root = FbRenderPack.getRootAsRenderPack(new flatbuffers.ByteBuffer(bytes))

  return {
    schemaVersion: normalizeString(root.schemaVersion()) as RenderPack["schemaVersion"],
    componentIds: readStringVector(root.componentIdsLength(), index => root.componentIds(index)),
    actionIds: readStringVector(root.actionIdsLength(), index => root.actionIds(index)),
    tokenIds: readStringVector(root.tokenIdsLength(), index => root.tokenIds(index)),
    bindingIds: readStringVector(root.bindingIdsLength(), index => root.bindingIds(index)),
    nodes: readTableVector(root.nodesLength(), index => root.nodes(index), readNodeTable),
    layoutOps: readTableVector(root.layoutOpsLength(), index => root.layoutOps(index), readLayoutOpTable),
    propOps: readTableVector(root.propOpsLength(), index => root.propOps(index), readPropOpTable),
    designOps: readTableVector(root.designOpsLength(), index => root.designOps(index), readDesignOpTable),
    bindings: readTableVector(root.bindingsLength(), index => root.bindings(index), readBindingTable),
    diagnostics: readTableVector(root.diagnosticsLength(), index => root.diagnostics(index), readDiagnosticTable),
  }
}

export function isRenderPackFlatBuffer(bytes: Uint8Array): boolean {
  if (bytes.length < 8) return false
  return FbRenderPack.bufferHasIdentifier(new flatbuffers.ByteBuffer(bytes))
}

function createNodeTable(builder: flatbuffers.Builder, node: RenderPackNode): flatbuffers.Offset {
  return FbNode.createNode(
    builder,
    builder.createString(node.id),
    node.componentId,
    node.parentIndex,
    FbNode.createChildIndicesVector(builder, node.childIndices),
    node.layoutOpStart,
    node.layoutOpCount,
    node.designOpStart,
    node.designOpCount,
    node.eventBindingStart,
    node.eventBindingCount,
    node.stateBindingStart,
    node.stateBindingCount,
    node.propOpStart,
    node.propOpCount,
  )
}

function createLayoutOpTable(builder: flatbuffers.Builder, op: RenderPackLayoutOp): flatbuffers.Offset {
  const encodedValue = encodePrimitiveValue(builder, op.value)
  return FbLayoutOp.createLayoutOp(
    builder,
    op.nodeIndex,
    builder.createString(op.kind),
    builder.createString(op.key),
    encodedValue.type,
    encodedValue.stringOffset,
    encodedValue.numberValue,
    encodedValue.booleanValue,
  )
}

function createDesignOpTable(builder: flatbuffers.Builder, op: RenderPackDesignOp): flatbuffers.Offset {
  const encodedValue = encodePrimitiveValue(builder, op.value)
  return FbDesignOp.createDesignOp(
    builder,
    op.nodeIndex,
    builder.createString(op.kind),
    builder.createString(op.key),
    encodedValue.type,
    encodedValue.stringOffset,
    encodedValue.numberValue,
    encodedValue.booleanValue,
  )
}

function createPropOpTable(builder: flatbuffers.Builder, op: RenderPackPropOp): flatbuffers.Offset {
  const encodedValue = encodePrimitiveValue(builder, op.value)
  return FbPropOp.createPropOp(
    builder,
    op.nodeIndex,
    builder.createString(op.kind),
    builder.createString(op.key),
    encodedValue.type,
    encodedValue.stringOffset,
    encodedValue.numberValue,
    encodedValue.booleanValue,
  )
}

function createBindingTable(builder: flatbuffers.Builder, binding: RenderPackBinding): flatbuffers.Offset {
  return FbBinding.createBinding(
    builder,
    binding.nodeIndex,
    builder.createString(binding.kind),
    builder.createString(binding.key),
    builder.createString(binding.target),
  )
}

function createDiagnosticTable(builder: flatbuffers.Builder, diagnostic: RenderPackDiagnostic): flatbuffers.Offset {
  return FbDiagnostic.createDiagnostic(
    builder,
    builder.createString(diagnostic.code),
    builder.createString(diagnostic.severity),
    builder.createString(diagnostic.message),
    diagnostic.nodeId ? builder.createString(diagnostic.nodeId) : 0,
    diagnostic.value ? builder.createString(diagnostic.value) : 0,
  )
}

function encodePrimitiveValue(
  builder: flatbuffers.Builder,
  value: RenderPackValue,
): { type: FbValueType; stringOffset: flatbuffers.Offset; numberValue: number; booleanValue: boolean } {
  if (typeof value === "number") {
    return { type: FbValueType.Number, stringOffset: 0, numberValue: value, booleanValue: false }
  }
  if (typeof value === "boolean") {
    return { type: FbValueType.Boolean, stringOffset: 0, numberValue: 0, booleanValue: value }
  }
  if (typeof value === "string") {
    return {
      type: FbValueType.String,
      stringOffset: builder.createString(value),
      numberValue: 0,
      booleanValue: false,
    }
  }
  return {
    type: FbValueType.Json,
    stringOffset: builder.createString(JSON.stringify(value)),
    numberValue: 0,
    booleanValue: false,
  }
}

function createStringVector(
  builder: flatbuffers.Builder,
  values: string[],
  createVector: (builder: flatbuffers.Builder, data: flatbuffers.Offset[]) => flatbuffers.Offset,
): flatbuffers.Offset {
  return createVector(
    builder,
    values.map(value => builder.createString(value)),
  )
}

function readNodeTable(node: FbNode): RenderPackNode {
  const childIndices: number[] = []
  for (let index = 0; index < node.childIndicesLength(); index++) {
    childIndices.push(node.childIndices(index) ?? 0)
  }

  return {
    id: normalizeString(node.id()),
    componentId: node.componentId(),
    parentIndex: node.parentIndex(),
    childIndices,
    layoutOpStart: node.layoutOpStart(),
    layoutOpCount: node.layoutOpCount(),
    designOpStart: node.designOpStart(),
    designOpCount: node.designOpCount(),
    eventBindingStart: node.eventBindingStart(),
    eventBindingCount: node.eventBindingCount(),
    stateBindingStart: node.stateBindingStart(),
    stateBindingCount: node.stateBindingCount(),
    propOpStart: node.propOpStart(),
    propOpCount: node.propOpCount(),
  }
}

function readLayoutOpTable(op: FbLayoutOp): RenderPackLayoutOp {
  return {
    nodeIndex: op.nodeIndex(),
    kind: normalizeString(op.kind()) as RenderPackLayoutOp["kind"],
    key: normalizeString(op.key()),
    value: readPrimitiveValue(op.valueType(), op.stringValue(), op.numberValue(), op.booleanValue()),
  }
}

function readDesignOpTable(op: FbDesignOp): RenderPackDesignOp {
  return {
    nodeIndex: op.nodeIndex(),
    kind: normalizeString(op.kind()) as RenderPackDesignOp["kind"],
    key: normalizeString(op.key()),
    value: readPrimitiveValue(op.valueType(), op.stringValue(), op.numberValue(), op.booleanValue()),
  }
}

function readPropOpTable(op: FbPropOp): RenderPackPropOp {
  return {
    nodeIndex: op.nodeIndex(),
    kind: normalizeString(op.kind()) as RenderPackPropOp["kind"],
    key: normalizeString(op.key()),
    value: readPrimitiveValue(op.valueType(), op.stringValue(), op.numberValue(), op.booleanValue()),
  }
}

function readBindingTable(binding: FbBinding): RenderPackBinding {
  return {
    nodeIndex: binding.nodeIndex(),
    kind: normalizeString(binding.kind()) as RenderPackBinding["kind"],
    key: normalizeString(binding.key()),
    target: normalizeString(binding.target()),
  }
}

function readDiagnosticTable(diagnostic: FbDiagnostic): RenderPackDiagnostic {
  const nodeId = normalizeOptionalString(diagnostic.nodeId())
  const value = normalizeOptionalString(diagnostic.value())
  return {
    code: normalizeString(diagnostic.code()),
    severity: normalizeString(diagnostic.severity()) as RenderPackDiagnostic["severity"],
    message: normalizeString(diagnostic.message()),
    ...(nodeId ? { nodeId } : {}),
    ...(value ? { value } : {}),
  }
}

function readPrimitiveValue(
  type: FbValueType,
  stringValue: string | Uint8Array | null,
  numberValue: number,
  booleanValue: boolean,
): RenderPackValue {
  if (type === FbValueType.Number) return numberValue
  if (type === FbValueType.Boolean) return booleanValue
  if (type === FbValueType.Json) return JSON.parse(normalizeString(stringValue)) as RenderPackValue
  return normalizeString(stringValue)
}

function readStringVector(length: number, read: (index: number) => string | Uint8Array | null): string[] {
  const values: string[] = []
  for (let index = 0; index < length; index++) values.push(normalizeString(read(index)))
  return values
}

function readTableVector<TTable, TValue>(
  length: number,
  read: (index: number) => TTable | null,
  map: (table: TTable) => TValue,
): TValue[] {
  const values: TValue[] = []
  for (let index = 0; index < length; index++) {
    const table = read(index)
    if (table) values.push(map(table))
  }
  return values
}

function normalizeString(value: string | Uint8Array | null): string {
  return normalizeOptionalString(value) ?? ""
}

function normalizeOptionalString(value: string | Uint8Array | null): string | undefined {
  if (typeof value === "string") return value
  if (value instanceof Uint8Array) return new TextDecoder().decode(value)
  return undefined
}
